import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import ButtonBase from '@material-ui/core/ButtonBase'
import Collapse from '@material-ui/core/Collapse'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import ExpandLessIcon from 'components/material-symbol/icons/ExpandLess'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import SyncIcon from 'components/material-symbol/icons/Sync'
import { CALLOUT_BACKGROUND, CALLOUT_FOREGROUND } from 'lib/form-field-styles'
import { CARD_HEADER_BORDER, cardHeaderStyles } from 'lib/styles/card-header'
import { PGA_COACH_NAVY } from 'lib/styles/pga-coach-pill-colors'
import { composeOptionInteractionStates } from 'pages/pga-coach/book-student/coach-book-student-compose-styles'
import { MESSAGE_TEMPLATES } from './message-templates'

const useStyles = makeStyles(theme => ({
  card: {
    border: `1px solid ${CARD_HEADER_BORDER}`,
    borderRadius: 12,
    backgroundColor: '#FAFBFC',
    overflow: 'hidden',
  },
  ...cardHeaderStyles(theme),
  header: {
    width: '100%',
    justifyContent: 'space-between',
    textAlign: 'left',
    padding: theme.spacing(2, 2.5),
    borderBottom: 'none',
    '&:focus-visible': {
      outline: `2px solid ${PGA_COACH_NAVY}`,
      outlineOffset: -2,
    },
  },
  title: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#172024',
  },
  chevron: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 2.5, 2.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  // Same card and icon chip as the destination cards in the calendar
  // "Export your PGA events" dialog (subscribe-dialog.js).
  tile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    boxSizing: 'border-box',
    padding: theme.spacing(2, 1.5),
    border: '1px solid #D5DDE3',
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    textAlign: 'center',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    ...composeOptionInteractionStates,
    '&:hover': {
      borderColor: '#9AA8B3',
      backgroundColor: theme.palette.common.white,
    },
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 50,
    height: 50,
    borderRadius: '50%',
    backgroundColor: CALLOUT_BACKGROUND,
    color: CALLOUT_FOREGROUND,
    '& .MuiSvgIcon-root, & svg': {
      width: 24,
      height: 24,
    },
  },
  tileLabel: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#172024',
  },
}))

// Collapsible row of starter messages above the Message editor, closed until
// the coach opens it. Picking one
// fills the editor; when the coach has already written something it asks
// before replacing it.
export default function QuickTemplates({ hasMessage, onApply }) {
  const classes = useStyles()
  const dialogClasses = useDialogTitleStyles()
  const [expanded, setExpanded] = useState(false)
  const [pending, setPending] = useState(null)

  const apply = template => {
    setPending(null)
    setExpanded(false)
    onApply(template)
  }

  const select = template => {
    if (hasMessage) setPending(template)
    else apply(template)
  }

  return (
    <Box>
      <Box className={classes.card} data-testid="campaign-quick-templates">
        <ButtonBase
          className={`${classes.cardHeader} ${classes.header}`}
          aria-expanded={expanded}
          aria-controls="campaign-quick-templates-panel"
          onClick={() => setExpanded(current => !current)}
        >
          <Typography component="h3" className={classes.title}>
            Quick Templates
          </Typography>
          {expanded ? (
            <ExpandLessIcon className={classes.chevron} />
          ) : (
            <ExpandMoreIcon className={classes.chevron} />
          )}
        </ButtonBase>
        <Collapse in={expanded} unmountOnExit>
          <Box
            id="campaign-quick-templates-panel"
            role="group"
            aria-label="Quick Templates"
            className={classes.tiles}
          >
            {MESSAGE_TEMPLATES.map(template => {
              const Icon = template.icon
              return (
                <ButtonBase
                  key={template.id}
                  className={classes.tile}
                  aria-label={`Use ${template.title} template`}
                  data-testid={`campaign-template-${template.id}`}
                  onClick={() => select(template)}
                >
                  <span className={classes.chip} aria-hidden="true">
                    <Icon />
                  </span>
                  <Typography component="span" className={classes.tileLabel}>
                    {template.title}
                  </Typography>
                </ButtonBase>
              )
            })}
          </Box>
        </Collapse>
      </Box>

      <Dialog
        isOpen={Boolean(pending)}
        onClose={() => setPending(null)}
        showCloseButton
        fullWidth
        maxWidth="xs"
        classes={{ paper: dialogClasses.dialogPaper }}
        aria-labelledby="campaign-template-replace-title"
      >
        {({ closeDialog }) => (
          <>
            <DialogTitle
              disableTypography
              classes={{ root: dialogClasses.titleRoot }}
            >
              <Typography
                id="campaign-template-replace-title"
                component="h2"
                className={dialogClasses.title}
              >
                Replace your message?
              </Typography>
              <Typography className={dialogClasses.bodyText}>
                {pending
                  ? `The ${pending.title} template will replace what you’ve written.`
                  : null}
              </Typography>
            </DialogTitle>
            <DialogActions className={dialogClasses.actions}>
              <Button color="primary" size="large" onClick={closeDialog}>
                Keep Message
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="large"
                disableElevation
                startIcon={<SyncIcon />}
                onClick={() => apply(pending)}
              >
                Replace
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

QuickTemplates.propTypes = {
  hasMessage: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
}
